import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import logo from '../assets/logo.JPG';
import { useScrollSpy } from '../hooks/useScrollSpy';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navItems = [
    { name: 'Home', href: '#home', id: 'home' },
    { name: 'About', href: '#about', id: 'about' },
    { name: 'Services', href: '#services', id: 'services' },
    { name: 'Inventory', href: '#inventory', id: 'inventory' },
    { name: 'Contact', href: '#contact', id: 'contact' },
  ];

  const activeId = useScrollSpy(navItems.map((item) => item.id));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-500 animate-navbar-enter ${
        scrolled
          ? 'bg-black/95 backdrop-blur-xl border-b border-[#3DED97]/15 shadow-lg shadow-black/20 py-0'
          : 'bg-transparent border-b border-transparent py-1'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex justify-between items-center transition-all duration-500 ${scrolled ? 'h-16' : 'h-20'}`}>
          <a href="#home" className="flex items-center gap-3 group">
            <img
              src={logo}
              alt="SharpCars Logo"
              className={`rounded-lg object-cover transition-all duration-500 group-hover:scale-105 ${
                scrolled ? 'h-12 w-20' : 'h-14 w-24'
              }`}
            />
            <span className="hidden sm:block font-display text-xl font-bold text-white group-hover:text-[#3DED97] transition-colors">
              Sharp<span className="gradient-text">Cars</span>
            </span>
          </a>

          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className={`nav-link px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                  activeId === item.id ? 'active text-[#3DED97]' : 'text-gray-400 hover:text-white'
                }`}
              >
                {item.name}
              </a>
            ))}
          </div>

          <div className="hidden md:block">
            <a href="tel:08066312634" className="btn-primary !px-6 !py-2.5 !text-sm !rounded-full">
              Call Now
            </a>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-gray-300 hover:text-[#3DED97] transition-colors duration-300"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden mobile-menu-enter pb-6">
            <div className="mt-2 p-4 rounded-2xl glass-card space-y-1">
              {navItems.map((item, i) => (
                <a
                  key={item.name}
                  href={item.href}
                  className={`block px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                    activeId === item.id
                      ? 'bg-[#3DED97]/10 text-[#3DED97]'
                      : 'text-gray-300 hover:bg-white/5 hover:text-white'
                  }`}
                  style={{ animationDelay: `${i * 50}ms` }}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </a>
              ))}
              <a
                href="tel:08066312634"
                className="block mt-3 btn-primary !w-full !text-center !py-3 !text-base"
                onClick={() => setIsOpen(false)}
              >
                Call Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
